import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { TranslationService } from '../../shared/translation/translation.service';
import { BonusesStatuses, GroupsService } from '../../services/groups.service';
import additionalSocketsFields, {CustomNotificationChanel } from './additional-socket-fields';
import { getRandomNumber } from '../../helpers/utils';
import { CookieService } from 'ngx-unificator/services';
import { UserService } from '../../services/user/user.service';
import { BlogService } from '../../../page/blog/blog.service';
import { BlogCategoryEnum } from '../../enums/blog-category.enum';
import { filterFirst } from 'ngx-unificator/rxjs';
import { CLEAR_BONUS_CODE } from '../../services/user/data/user-group.data';
import { LanguageService } from '../../services/language/language.service';
import { UserBonusesService } from '../../services/user/user-bonuses.service';

export const RELOAD_BONUSES_TIME_TO_SHOW = {
  MONDAY_BONUS: {
    allowed_always: false,
    allowed_days: [1],
    title: 't.monday-bonus-title',
    description: 't.monday-bonus-description',
    prize: 't.monday-bonus-prize',
    condition: 't.monday-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/monday.png',
    bonusCode: CLEAR_BONUS_CODE,
    active: false,
  },
  FRIDAY_BONUS: {
    allowed_always: false,
    allowed_days: [5],
    title: 't.friday-bonus-title',
    description: 't.friday-bonus-description',
    prize: 't.friday-bonus-prize',
    condition: 't.friday-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/friday.png',
    bonusCode: CLEAR_BONUS_CODE,
    active: false,
  },
  TOP_SLOTS: {
    allowed_days: null,
    allowed_always: true,
    title: 't.top-slots-notify-title',
    description: 't.top-slots-notify-desc',
    prize: 't.top-slots-bonus-prize',
    condition: 't.top-slots-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/top-slots.png',
    bonusCode: 'TOP40',
    active: false,
  },
  WEEKEND_RELOAD: {
    allowed_always: false,
    allowed_days: [0, 6],
    title: 't.weekend-notify-title',
    description: 't.weekend-notify-desc',
    prize: 't.weekend-bonus-prize',
    condition: 't.weekend-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/weekend.png',
    bonusCode: 'SUSHI',
    active: false,
  },
};

type Uid = {
  key: string;
  uid: number | string;
}


@Injectable({
  providedIn: 'root',
})
export class CustomNotificationCenterService {
  private _translate = inject(TranslationService);
  private _groups = inject(GroupsService);
  private _user = inject(UserService);
  private _cookie = inject(CookieService);
  private _blog = inject(BlogService);
  private _lang = inject(LanguageService);
  private _bonuses = inject(UserBonusesService);

  /**
   * Use for install app custom chanel
   * @private
   */
  private _installAppChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _mascotChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _vipChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _welcomeBonusesChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _reloadBonusesChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _blogChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);

  private _previousBlogUid: number;
  private _previousWelcomeUid: number;

  private _oldReloadUidsList: Uid[] = [];

  private _customReplaySubjectEmptyDataList = [
    this._installAppChannel$, this._mascotChannel$, this._vipChannel$
  ]

  private _resolvedWelcomeBonusesChannel$(): Observable<any> {
   return this._bonuses.getMergedBonuses$().pipe(
     filter(() => !this._cookie.check(`welcome-bonus-${this._user.stats?.deposits_count}`)),
     map(bonuses => bonuses.find(item => item.active)),
     distinctUntilChanged((a, b) => a?.id === b?.id),
     filter(bonus => !!bonus),
     map(bonus => {
       this._previousWelcomeUid = this._previousWelcomeUid || getRandomNumber(111, 111111111111111);

       return {
         ...bonus,
         notify_channel: CustomNotificationChanel.WELCOME_BONUSES,
         uid: this._previousWelcomeUid,
         ...additionalSocketsFields[CustomNotificationChanel.WELCOME_BONUSES],
         icon: bonus?.Image,
         title: bonus?.DescriptionModal,
         description: bonus?.Description + ' ' + bonus?.SecondDescription,
         analyticClass: 'devss-7090-welcome',
       }
     }),
     tap(bonus => this._welcomeBonusesChannel$.next(bonus)),
   );
  }

  private _resolvedReloadBonusesChannel$() {
    return of(this.getActiveReloadBonusNotification()).pipe(
      filter(() => !this._cookie.check('reload-bonus')),
      map(notifications =>
        notifications.map(n => {
          const oldUid = this._getOldUidForReloadBonuses(n?.title);
          const uid = getRandomNumber(111, 111111111111111);
          if (!oldUid) {
            this._oldReloadUidsList.push({key: n?.title, uid});
          }

          return {
            ...n,
            notify_channel: CustomNotificationChanel.RELOAD_BONUSES,
            uid: oldUid || uid,
            ...additionalSocketsFields[CustomNotificationChanel.RELOAD_BONUSES],
            description: this._translate.translate(n.description),
            linkParams: {
              bonusCode: n?.bonusCode,
            },
          }
        }),
      ),
      tap(bonus => this._reloadBonusesChannel$.next(bonus)),
    )
  }

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolvedInstallAppChannel$: Observable<any> = this._installAppChannel$.pipe(
    distinctUntilChanged(),
    map(() => ({
      notify_channel: CustomNotificationChanel.INSTALL_APP,
      uid: getRandomNumber(111, 111111111111111),
      link: '/mobile-app',
      description: this._translate.translate('t.all-games-bonuses-app'),
      ...additionalSocketsFields[CustomNotificationChanel.INSTALL_APP],
    })),
  );

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolvedMascothannel$: Observable<any> = this._mascotChannel$.pipe(
    distinctUntilChanged(),
    map(() => ({
      notify_channel: CustomNotificationChanel.MASCOT_NOTIFY,
      uid: getRandomNumber(111, 111111111111111),
      link: '/profile/account',
      description: this._translate.translate('t.mascot-notify-desc'),
      title: 't.mascot-notify-title',
      icon: '/assets/img/notification/mascot.png',
      templateTitle: 't.mascot-notify-btn',
      ...additionalSocketsFields[CustomNotificationChanel.MASCOT_NOTIFY],
    })),
  );

  private _resolvedVipChannel$: Observable<any> = this._vipChannel$.pipe(
    map(() => ({
      notify_channel: CustomNotificationChanel.VIP,
      uid: getRandomNumber(111, 111111111111111),
      title: this._translate.translate('t.vip-notification-title'),
      link: '/vip-program',
    })),
  );

  private _blogNotification$() {
    return this._blog.list({
      type: BlogCategoryEnum.NOTIFICATIONS,
    }).pipe(
      filterFirst(data => !!data),
      map(data => data?.list?.filter(article => !this._cookie.check(article?.slug))),
      map(data => data?.map(b => {
        this._previousBlogUid = this._previousBlogUid || getRandomNumber(111, 111111111111111);
        return {
          ...b,
          notify_channel: CustomNotificationChanel.BLOG,
          ...additionalSocketsFields[CustomNotificationChanel.BLOG],
          description: b.Description,
          title: b.Title,
          uid: this._previousBlogUid,
          icon: b.Image || additionalSocketsFields[b.notify_channel]?.icon,
          link: b.Link,
        }
      })),
      tap(data => this._blogChannel$.next(data)),
    );
  }

  public notificationSubjectList$ = [
    this._resolvedInstallAppChannel$,
    this._resolvedMascothannel$,
    this._resolvedVipChannel$,
    this._welcomeBonusesChannel$,
    this._reloadBonusesChannel$,
    this._blogChannel$
  ];


  constructor() {}

  get installAppChannel$() {
    return this._installAppChannel$;
  }
  get mascotChannel$() {
    return this._mascotChannel$;
  }

  get vipChannel$() {
    return this._vipChannel$;
  }
  get welcomeBonusesChannel$() {
    return this._welcomeBonusesChannel$;
  }
  get reloadBonusesChannel$() {
    return this._reloadBonusesChannel$;
  }

  public getActiveReloadBonusNotification(date = new Date()) {
    if (this._groups.isExistGroup(
      BonusesStatuses.ALL_WELCOME_OFFERS_USED) || this._groups.isExistGroup(
      BonusesStatuses.ALL_SPECIAL_WELCOME_USED)
    ) {
      const day = date.getUTCDay();

      return Object.entries(RELOAD_BONUSES_TIME_TO_SHOW)
        .filter(([_, bonus]) =>
          bonus.allowed_always || bonus.allowed_days.includes(day))
        .map(([_, { title, description, bonusCode, prize, condition, active, image }], idx) => ({
          title,
          description,
          bonusCode,
          prize,
          condition,
          active,
          image,
        }));
    } else {
      return [];
    }
  }

  public initCustomNotifications() {
    this._lang.langChange$.pipe(
      tap(() => {
        this._resolvedWelcomeBonusesChannel$().subscribe();
        this._resolvedReloadBonusesChannel$().subscribe();
        this._blogNotification$().subscribe();
        this._triggerEmptyReplaySubjectList()
      }),

    ).subscribe(lang => {})
  }

  private _triggerEmptyReplaySubjectList() {
    this._customReplaySubjectEmptyDataList.forEach(async (item) => {
      const value =  await firstValueFrom(item);
      item.next(value);
    })
  }

  private _getOldUidForReloadBonuses(key: string) {
    return this._oldReloadUidsList?.find(l => l.key === key)?.uid;
  }
}
